import type { ReactNode } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { TranslationKey, useTar } from '@/modules'
import { cn } from '@duck/ui'
import { PostgresIcon } from '../Workspace/WorkspaceSettings/panels/icons/PostgresIcon'
import { PythonIcon } from '../Workspace/WorkspaceSettings/panels/icons/PythonIcon'

const WEAPON_ICON: Record<ChallengeService.TaskWeaponsOfChoiceEnum, ReactNode> = {
  [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: <PostgresIcon className="h-5 w-5" />,
  [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: <PythonIcon className="h-5 w-5" />,
}

const WEAPON_LABEL_KEY: Record<ChallengeService.TaskWeaponsOfChoiceEnum, TranslationKey> = {
  [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'common.settingsLanguageSql',
  [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'common.settingsLanguagePython',
}

export interface ProofOfAchievementProps {
  name: string
  taskName: string
  attemptNumber: number
  grade: number
  submittedAt: Date
  percentile: number | null
  workTime: number | null // minutes
  memberSince: Date | null
  feedbackExcerpt: string | null
  weaponOfChoice: ChallengeService.TaskWeaponsOfChoiceEnum | null
  // Org credit for assignment-linked submissions ("Submitted for: <wide logo>"); null otherwise.
  organization: { name: string; logoURL?: string | null } | null
  // Extra classes merged onto the card root (lets callers override chrome, e.g. full-bleed on mobile).
  className?: string
}

const formatDate = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
const formatMonth = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
const formatMinutes = (m: number) => {
  const h = Math.floor(m / 60)
  const min = m % 60
  return h > 0 ? `${h}h ${min}m` : `${min}m`
}
// English ordinal: 2 → "2nd", 3 → "3rd", 4 → "4th", 21 → "21st" (n=1 handled separately as a word)
const ordinal = (n: number) => {
  const tens = n % 100
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'
  return `${n}${suffix}`
}

export const ProofOfAchievement = (props: ProofOfAchievementProps) => {
  const { t } = useTar()
  const hasPercentile = props.percentile != null
  const hasWorkTime = props.workTime != null
  const hasMemberSince = props.memberSince != null
  const hasExcerpt = !!props.feedbackExcerpt

  return (
    <div className={cn("relative max-w-[720px] mx-auto bg-card border border-border rounded-xl p-9 px-12 shadow-[0_25px_55px_-22px_rgba(0,0,0,0.18),0_6px_16px_-10px_rgba(0,0,0,0.12)] dark:shadow-[0_30px_70px_-20px_rgba(0,0,0,0.65),0_8px_20px_-10px_rgba(0,0,0,0.4)]", props.className)}>
      {/* Decorative corner brackets */}
      <span aria-hidden className="absolute top-3 left-3 w-8 h-8 border-t border-l border-primary/55" />
      <span aria-hidden className="absolute bottom-3 right-3 w-8 h-8 border-b border-r border-primary/55" />

      <div className="text-center text-[11px] tracking-[4px] text-primary font-bold mt-1">
        {t('share.publicPage.eyebrow')}
      </div>
      <div className="w-14 h-[2px] bg-primary opacity-70 rounded mx-auto mt-2 mb-5" />

      <div className="text-center text-3xl font-semibold tracking-tight font-serif">{props.name}</div>
      <div className="text-center text-xs text-muted-foreground italic my-1">
        — {t('share.publicPage.completed')} —
      </div>
      <div className="text-center text-xl font-semibold text-foreground">{props.taskName}</div>

      {props.weaponOfChoice && (
        <div className="flex items-center justify-center gap-2 mt-3 text-sm text-muted-foreground">
          <span>{t('share.publicPage.solvedWith')}</span>
          {WEAPON_ICON[props.weaponOfChoice]}
          <span className="font-semibold text-foreground">
            {t(WEAPON_LABEL_KEY[props.weaponOfChoice])}
          </span>
        </div>
      )}

      <div className={`grid ${hasPercentile ? 'grid-cols-[1fr_auto_1fr]' : 'grid-cols-1'} gap-5 items-center my-7`}>
        <div className="flex flex-col items-center justify-center w-[130px] h-[130px] mx-auto rounded-full border-2 border-foreground">
          <div className="text-[9px] tracking-[2px] text-muted-foreground font-bold">GRADE</div>
          <div className="text-4xl font-serif font-bold leading-none mt-1">
            {props.grade}
            <span className="text-lg text-muted-foreground">/10</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {props.attemptNumber <= 1
              ? t('share.publicPage.firstAttempt')
              : t('share.publicPage.nthAttempt', { ord: ordinal(props.attemptNumber) })}
          </div>
        </div>
        {hasPercentile && <div className="w-px h-[80%] bg-border" />}
        {hasPercentile && (
          <div className="flex flex-col items-center justify-center w-[130px] h-[130px] mx-auto rounded-full border-2 border-primary bg-primary/10">
            <div className="text-[9px] tracking-[2px] text-primary font-bold">{t('share.toggle.percentile').toUpperCase()}</div>
            <div className="text-3xl font-serif font-bold leading-none mt-1 text-primary">
              {props.percentile}
              <span className="text-sm">th</span>
            </div>
          </div>
        )}
      </div>

      {props.organization && (
        <div className="flex flex-col items-center gap-2 -mt-2 mb-7">
          <span className="text-[9px] tracking-[2px] uppercase text-muted-foreground font-bold">
            {t('share.publicPage.submittedFor')}
          </span>
          {props.organization.logoURL ? (
            <img
              src={props.organization.logoURL}
              alt={props.organization.name}
              className="h-9 max-w-[220px] object-contain"
            />
          ) : (
            <span className="text-base font-semibold text-foreground">{props.organization.name}</span>
          )}
        </div>
      )}

      <div className="flex justify-center gap-7 flex-wrap text-sm text-muted-foreground border-t border-b border-border/60 py-3 mb-4">
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-[9px] tracking-[1px] uppercase">{t('share.publicPage.submitted')}</span>
          <b className="text-foreground font-semibold">{formatDate(props.submittedAt)}</b>
        </div>
        {hasWorkTime && (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[9px] tracking-[1px] uppercase">{t('share.toggle.workTime')}</span>
            <b className="text-foreground font-semibold">{formatMinutes(props.workTime!)}</b>
          </div>
        )}
        {hasMemberSince && (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[9px] tracking-[1px] uppercase">{t('share.toggle.memberSince')}</span>
            <b className="text-foreground font-semibold">{formatMonth(props.memberSince!)}</b>
          </div>
        )}
      </div>

      {hasExcerpt && (
        <blockquote className="italic text-foreground text-center max-w-[480px] mx-auto leading-relaxed mb-5 before:content-['\201C'] before:text-primary before:text-2xl before:align-[-8px] before:mr-1 after:content-['\201D'] after:text-primary after:text-2xl after:align-[-12px] after:ml-1">
          {props.feedbackExcerpt}
        </blockquote>
      )}

      <div className={cn("flex justify-between items-center border-border/60 pt-3 text-xs text-muted-foreground", hasExcerpt && "border-t")}>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-[10px] font-bold">
            ✓
          </span>
          {t('share.publicPage.verified')}
        </div>
      </div>
    </div>
  )
}
